/**
 * Праздничные моменты · Ф2.3 — перенос из design/phase2-prototype.html.
 *
 * Направление, принятое на воротах вида: фон спокоен («Минимал»), движение — только
 * на моментах успеха. Здесь три вещи и ничего больше:
 *   • конфетти на canvas (лёгкий: точки считаем сами, библиотек не тянем);
 *   • всплывающая похвала («Верно!», «Так держать!») поверх экрана;
 *   • салют на финале занятия.
 *
 * ⚠ «Поменьше движения» уважаем жёстко: при prefers-reduced-motion конфетти и салют
 * не запускаются вообще, похвала остаётся (это текст, а не движение).
 * Слои создаются лениво и живут внутри .shell (у неё position: relative).
 */

const reduced = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const CONFETTI = ["#1f9d74", "#57c79a", "#e0a53a", "#f2c33e", "#d9695f", "#ffffff"];

interface Part {
  x: number; y: number; vx: number; vy: number; g: number;
  s: number; rot: number; vr: number; age: number; life: number; c: string;
}

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let toastHost: HTMLElement | null = null;
let parts: Part[] = [];
let raf = 0;

/** Слои поверх приложения. Создаём один раз при первом празднике. */
function layers(): void {
  const shell = document.getElementById("shell");
  if (!shell) return;
  if (!canvas) {
    canvas = document.createElement("canvas");
    canvas.className = "fx";
    canvas.setAttribute("aria-hidden", "true");
    shell.appendChild(canvas);
    window.addEventListener("resize", sizeCanvas);
  }
  if (!toastHost) {
    toastHost = document.createElement("div");
    toastHost.className = "toasthost";
    // Похвала — это сообщение о результате: слепой пользователь тоже должен его получить.
    toastHost.setAttribute("role", "status");
    toastHost.setAttribute("aria-live", "polite");
    shell.appendChild(toastHost);
  }
}

function sizeCanvas(): void {
  if (!canvas) return;
  const r = canvas.getBoundingClientRect();
  if (!r.width) return;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = r.width * dpr;
  canvas.height = r.height * dpr;
  ctx = canvas.getContext("2d");
  ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function spawn(cx: number, cy: number, n: number, power: number): void {
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2;
    const sp = power * (0.4 + Math.random());
    parts.push({
      x: cx, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - power * 0.55,
      g: 0.13 + Math.random() * 0.06, s: 5 + Math.random() * 5,
      rot: Math.random() * 6, vr: (Math.random() - 0.5) * 0.4,
      age: 0, life: 56 + Math.random() * 34,
      c: CONFETTI[(Math.random() * CONFETTI.length) | 0],
    });
  }
  if (!raf) raf = requestAnimationFrame(tick);
}

function tick(): void {
  if (!ctx || !canvas) { raf = 0; return; }
  const w = canvas.clientWidth, h = canvas.clientHeight;
  ctx.clearRect(0, 0, w, h);
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i];
    p.age++;
    if (p.age >= p.life) { parts.splice(i, 1); continue; }
    p.vy += p.g; p.x += p.vx; p.y += p.vy; p.rot += p.vr;
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.globalAlpha = Math.max(0, 1 - p.age / p.life);
    ctx.fillStyle = p.c;
    ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 0.62);
    ctx.restore();
  }
  raf = parts.length ? requestAnimationFrame(tick) : 0;
}

/** Хлопок конфетти из точки элемента (или из центра экрана, если элемента нет). */
export function burst(el?: Element | null, n = 20, power = 6): void {
  if (reduced()) return;
  layers();
  sizeCanvas();
  if (!canvas) return;
  const fr = canvas.getBoundingClientRect();
  if (!fr.width) return;
  let cx = fr.width / 2, cy = fr.height * 0.4;
  if (el) {
    const er = el.getBoundingClientRect();
    cx = er.left + er.width / 2 - fr.left;
    cy = er.top + er.height / 2 - fr.top;
  }
  spawn(cx, cy, n, power);
}

/** Салют на финале занятия — три очага. */
export function salute(): void {
  if (reduced()) return;
  layers();
  sizeCanvas();
  if (!canvas) return;
  const fr = canvas.getBoundingClientRect();
  if (!fr.width) return;
  spawn(fr.width * 0.22, fr.height * 0.52, 28, 8);
  spawn(fr.width * 0.78, fr.height * 0.52, 28, 8);
  spawn(fr.width * 0.5, fr.height * 0.34, 26, 7);
}

const PHRASES = ["Отлично! 👏", "Так держать! 🔥", "Верно! 🎉", "Красота! ✨", "Молодец! 🌱", "Есть! 💪"];
let phrase = 0;

/** Всплывающая похвала. Без текста — берём следующую по кругу. */
export function praise(text?: string): void {
  layers();
  if (!toastHost) return;
  const t = document.createElement("div");
  t.className = "toast";
  t.textContent = text || PHRASES[phrase++ % PHRASES.length];
  toastHost.appendChild(t);
  setTimeout(() => t.remove(), 1700);
}

/** Убрать всё лишнее с экрана (уход с занятия). */
export function clearFx(): void {
  parts = [];
  if (ctx && canvas) ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
  if (toastHost) toastHost.innerHTML = "";
}
